<template>
  <header class="app-header">
    <div class="header-content">
      <nav class="nav">
        <router-link to="/all" class="nav-link" :class="{ active: isActive('/all') }">{{ $t('nav.all') }}</router-link>
        <router-link to="/next-draws" class="nav-link" :class="{ active: isActive('/next-draws') }">{{ $t('nav.nextDraws') }}</router-link>
        <router-link v-if="showWagers" to="/wagers" class="nav-link" :class="{ active: isActive('/wagers') }">{{ $t('nav.wagers') }}</router-link>
        <router-link to="/results" class="nav-link" :class="{ active: isActive('/results') }">{{ $t('nav.results') }}</router-link>
        <router-link to="/how-to-play" class="nav-link" :class="{ active: isActive('/how-to-play') }">{{ $t('nav.howToPlay') }}</router-link>
      </nav>
      
      <div class="cart-section desktop-only">
        <div class="cart-icon" @click="openBetslip">
          <img src="/img/icons/cart.svg" alt="Cart" class="cart-svg" />
          <span v-if="betCount > 0" class="bet-count">{{ betCount }}</span>
        </div>
      </div>
      <div class="mobile-actions mobile-only">
        <div class="cart-icon" @click="openBetslip">
          <img src="/img/icons/cart.svg" alt="Cart" class="cart-svg" />
          <span v-if="betCount > 0" class="bet-count">{{ betCount }}</span>
        </div>
        </div>
      </div>
  </header>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useBetslipStore } from '../../stores/betslip'
import { useModalStore } from '../../stores/modal'
import { useTheme } from '../../composables/useTheme'

const route = useRoute()
const authStore = useAuthStore()
const betslipStore = useBetslipStore()
const modalStore = useModalStore()
const { currentTheme } = useTheme()

const betCount = computed(() => betslipStore.betslip?.length || 0)

const isActive = (path: string) => route.path === path

const openBetslip = () => {
  modalStore.openBetslip()
}

const showWagers = computed(() => {
  const hideWhenUnauthenticated = currentTheme.value?.navigation?.hideWagersWhenUnauthenticated
  if (hideWhenUnauthenticated) {
    return authStore.isAuthenticated || authStore.hasValidSession()
  }
  return true
})

onMounted(() => {
  authStore.initializeFromSession()
})
</script>

<style scoped>

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.nav {
  display: flex;
  gap: 48px;
  padding: 16px 0;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  flex: 1;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .nav {
    gap: 0;
    padding: 16px 0;
    margin: 0;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
    max-width: 100vw;
    width: 100%;
  }
  
  .nav::-webkit-scrollbar {
    display: none;
  }

  .nav-link {
    white-space: nowrap;
    scroll-snap-align: start;
    flex-shrink: 0;
    width: auto !important;
    text-align: center;
    padding: 8px 12px !important;
    min-width: 30%;
    max-width: none;
    width: 100%;
  }
  
  .auth-indicator {
    font-size: 9px;
    padding: 1px 4px;
    min-width: 20px;
  }
}

.nav-link {
  color: var(--text-primary);
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  opacity: 0.7;
  transition: all 0.2s ease;
  position: relative;
  padding-bottom: 16px;
  width: 100%;
  text-align: center;
}

.nav-link:hover {
  opacity: 1;
}

.nav-link.active {
  opacity: 1;
  color: var(--text-nav-link-active, var(--accent-color-link-active));
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--accent-color-link-active);
}

.cart-section {
  display: flex;
  align-items: center;
  position: relative;
}

/* Desktop only - show cart */
.desktop-only {
  display: block;
}

/* Mobile only - hide cart */
.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
  
  .mobile-only {
    display: block;
  }
}

.cart-icon {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  color: var(--text-primary);
}

.cart-icon:hover {
  transform: scale(1.05);
}

.cart-svg {
  width: 24px;
  height: 24px;
  filter: var(--filter-light, none);
  transition: filter 0.2s ease;
}

.theme-dark .cart-svg {
  filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
}

.bet-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--bg-bet-count, #00C853);
  color: var(--text-bet-count, white);
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  height: 20px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 2px solid #1A1F2E;
}

.mobile-actions {
  display: none;
  align-items: center;
  gap: 12px;
}

.theme-toggle-mobile {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  color: var(--text-primary);
}

.theme-toggle-mobile:hover {
  transform: scale(1.05);
}

.theme-toggle-mobile .toggle-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 2px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  box-shadow: 0 2px 8px var(--shadow-color);
  transition: all 0.3s ease;
}

.theme-toggle-mobile:hover .toggle-icon {
  background: var(--accent-color);
  color: var(--text-white);
  border-color: var(--accent-color);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }
  
  .nav {
    flex: 1;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
  }
  
  .mobile-actions {
    display: flex;
    flex-shrink: 0;
    margin-left: 16px;
    gap: 8px;
  }
  
  .mobile-actions .cart-icon {
    width: 32px;
    height: 32px;
  }
  
  .mobile-actions .cart-svg {
    width: 18px;
    height: 18px;
  }
  
  .mobile-actions .bet-count {
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    top: -6px;
    right: -1px;
  }
}
</style> 